import type { RestaurantZone } from '@agos/api-client/model'
import { tt } from '@agos/shared'

import type { RestaurantTable } from './types'

/**
 * The floor plan's zones - `FRONTEND.md` §9.337, `BACKEND.md` §10A.117 / §10A.125.
 *
 * **Two lists of zones exist and both have to be drawn.** `restaurant_tables.zone` is free text a
 * franchise typed per table - NULL on all 11 of the owner's rows, which is the measurement that made
 * §10A.117 build a real `restaurant_zones` table. The real zone is what a register hangs off; the
 * text is what somebody may already have. Replacing the text would break a franchise that used it,
 * which is exactly why the backend added `zone_id` *beside* it.
 *
 * So everything here merges the two with the real zone winning, and none of it knows about React.
 */

/**
 * The right a zone's writes ask - FRONTEND.md §9.758, BACKEND.md §10A.413 (owner, folded into O119).
 *
 * `POST`, `PATCH` and `DELETE /restaurant-zones` were `@RequireAuth("main_admin")` and the table map followed with
 * `systemRole === 'main_admin'`. They ask `restaurant_edit` now - **the write key alone**, because `restaurant`, the
 * screen's own key, is held by line roles on the owner's data (Šurany's waiter, Nesvady's cook and waiter) and pairing
 * the two would hand them which register prints a zone's receipts.
 */
export const RESTAURANT_EDIT = 'restaurant_edit'

/** Whether this person is offered *Zóny a pokladne* - asked of the session's own `hasPermission`. */
export function mayEditZones(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission(RESTAURANT_EDIT)
}

/** A zone as the chips and the pickers want it, from either source. */
export interface ZoneOption {
  /** The real zone's id, or null for one that exists only as text on a table. */
  id: number | null
  name: string
  color: string | null
  /** The register this zone's receipts print on - §10A.125. Null means *follow the channel*. */
  kasaId: string | null
  /**
   * The sales channel a new order opens on for a table in this zone - §10A.332, owner O43.
   *
   * Null means *no opinion*, and then the screen keeps its own default. Deliberately a slug rather
   * than a resolved channel: the stored value may name one that was later switched off or renamed, so
   * whoever draws it resolves it against the channels they have loaded and falls back.
   */
  salesChannel: string | null
  /** How many of the tables passed in belong to it. */
  tables: number
}

export function zonesFrom(payload: unknown): RestaurantZone[] {
  const zones = (payload as { zones?: unknown } | undefined)?.zones
  return Array.isArray(zones) ? (zones as RestaurantZone[]) : []
}

/**
 * Whether this table is in this zone.
 *
 * **`zoneId` first, the text second, and the order is the whole rule.** A table put in a real zone
 * may still carry the old text - §10A.117 left it there on purpose - and in that case the real zone
 * is the answer. Matching on the text first would file such a table under whatever it used to say.
 */
export function tableIsIn(table: RestaurantTable, zone: ZoneOption): boolean {
  const tableZoneId = table.zoneId ?? null
  if (zone.id !== null) return tableZoneId === zone.id
  // A text-only zone claims a table only when that table has no real zone at all, or the same table
  // would be counted twice - once under its zone and once under its leftover string.
  return tableZoneId === null && (table.zone ?? '').trim() === zone.name
}

/**
 * Every zone worth offering: the real ones, then any free text the tables still carry.
 *
 * **A real zone with no tables is still offered**, deliberately. A franchise sets its zones up before
 * it assigns tables to them, and a chip that appeared only after the first table was assigned would
 * read as the zone having failed to save.
 *
 * A text-only zone whose name matches a real one is dropped rather than listed twice - that is the
 * state a franchise is in halfway through migrating off the text column.
 */
export function zoneOptions(
  zones: readonly RestaurantZone[],
  tables: readonly RestaurantTable[],
): ZoneOption[] {
  const real: ZoneOption[] = zones
    .flatMap((zone) => {
      // A row with no id or no name cannot be selected or written to, so it is not offered. The
      // generated type makes both optional because the wire schema does; the server never sends
      // either absent, and a zone that slipped through would be a chip that patches nothing.
      const id = zone.id
      const name = (zone.name ?? '').trim()
      if (typeof id !== 'number' || name === '') return []
      return [
        {
          id,
          name,
          color: zone.color ?? null,
          kasaId: zone.kasaId ?? null,
          salesChannel: zone.salesChannel ?? null,
          tables: 0,
        },
      ]
    })

  const takenNames = new Set(real.map((zone) => zone.name.toLowerCase()))
  const textOnly: ZoneOption[] = []
  for (const table of tables) {
    // **Only a table with no real zone contributes a text zone**, matching `tableIsIn`. A table
    // moved into a real zone keeps its old string - §10A.117 does not clear it - and harvesting
    // that string would offer a chip for a zone with **no tables in it**, which is a zone that
    // looks real and filters to an empty floor. Found by this file's own test.
    if (table.zoneId != null) continue
    const text = (table.zone ?? '').trim()
    if (text === '' || takenNames.has(text.toLowerCase())) continue
    takenNames.add(text.toLowerCase())
    textOnly.push({
      id: null,
      name: text,
      color: null,
      kasaId: null,
      salesChannel: null,
      tables: 0,
    })
  }

  const all = [...real, ...textOnly.sort((left, right) => left.name.localeCompare(right.name, 'sk'))]
  return all.map((zone) => ({
    ...zone,
    tables: tables.filter((table) => tableIsIn(table, zone)).length,
  }))
}

/**
 * The channel a new order should open on for this table - §9.660, owner O43.
 *
 * **Resolved against the channels the screen has loaded, never trusted as a slug.** §10A.332 is deliberately not a
 * foreign key, so a zone may point at a channel that was later switched off or renamed - and the honest answer then
 * is the screen's own default rather than a channel on which no order can be placed. The list passed in is
 * `orderableChannels`', the same one the builder draws its chips from, so the two cannot disagree about what is on
 * offer.
 *
 * **A table with no zone, a zone with no opinion and a text-only zone all answer the fallback**, which is what every
 * table did before this field existed.
 */
export function channelForTable(
  table: RestaurantTable,
  zones: readonly ZoneOption[],
  channels: readonly { slug: string }[],
  fallback: string,
): string {
  const zone = zones.find((candidate) => candidate.id !== null && tableIsIn(table, candidate))
  const chosen = (zone?.salesChannel ?? '').trim()
  if (chosen === '') return fallback
  return channels.some((channel) => channel.slug === chosen) ? chosen : fallback
}

/** The tables a chip should show. An empty selection is every table, as it always was. */
export function tablesInZone(
  tables: readonly RestaurantTable[],
  selected: ZoneOption | null,
): RestaurantTable[] {
  if (selected === null) return [...tables]
  return tables.filter((table) => tableIsIn(table, selected))
}

/** Matching a chip back to its option, since the id may be null for a text-only zone. */
export function zoneKey(zone: ZoneOption): string {
  return zone.id === null ? `t:${zone.name}` : `z:${zone.id}`
}

export function findZone(zones: readonly ZoneOption[], key: string): ZoneOption | null {
  return zones.find((zone) => zoneKey(zone) === key) ?? null
}

export type ZoneNameProblem = 'empty' | 'too-long' | 'duplicate'

/**
 * What stops a zone name being saved - the server's own three rules, stated here so the form can say
 * so before the request.
 *
 * `RestaurantZoneService.requireName` trims, refuses empty and refuses over 40 characters; the unique
 * index is on `lower(name)`, so *Terasa* and *terasa* are the same place. **Duplicate is checked
 * case-insensitively for that reason** - a form that only compared exactly would send a request the
 * database refuses with a constraint name.
 */
export function zoneNameProblem(
  name: string,
  zones: readonly ZoneOption[],
  editingId: number | null = null,
): ZoneNameProblem | null {
  const trimmed = name.trim()
  if (trimmed === '') return 'empty'
  if (trimmed.length > 40) return 'too-long'
  const clash = zones.some(
    (zone) => zone.id !== editingId && zone.name.toLowerCase() === trimmed.toLowerCase(),
  )
  return clash ? 'duplicate' : null
}

export function zoneNameMessage(problem: ZoneNameProblem): string {
  if (problem === 'empty') return tt('Názov zóny je povinný')
  if (problem === 'too-long') return tt('Názov zóny je príliš dlhý - najviac 40 znakov')
  return tt('Zóna s týmto názvom už existuje')
}

/**
 * What a zone's register choice reads as when nothing is chosen.
 *
 * **Not blank.** Null is a real answer - *follow the channel*, which is §10A.125's fall-through and
 * what every zone does today - and a blank option reads as *nobody has got round to it*.
 */
export const FOLLOWS_CHANNEL = tt('Podľa kanála')

/**
 * The empty option on the zone's channel select - §9.660, owner O43.
 *
 * *Podľa obrazovky* rather than *Podľa kanála*: the fallback is whatever the screen that opened the order builder
 * would have sent anyway, which for a table tap is `restaurant` and for the bar's button is `bar`. Naming the channel
 * would claim the zone follows one.
 */
export const FOLLOWS_SCREEN = tt('Podľa obrazovky')
